import { useMsal } from '@azure/msal-react';
import { authStorage } from '@brightdrop/auth-client';
import { useEffect } from 'react';

import type { BDError } from '~/common/models/error.model';
import {
  LoginRead,
  logUserReadFailure,
  setLoginAccountContext,
} from '~/common/utils/analytics/login-telemetry';

type UserReadTelemetryParams = {
  /** The failed profile read, as mirrored into the profile slice. */
  profileError?: BDError;
  /** The failed permissions read, as mirrored into the profile slice. */
  permissionsError?: BDError;
  /** True while the profile read is in flight. */
  isProfileFetching: boolean;
};

/**
 * Reports the signed-in user's reads to Datadog: tags every log with the MSAL
 * account the reads were issued for, then logs each read that failed.
 * @param params - The failed reads and whether the profile is still loading
 */
export const useUserReadTelemetry = ({
  profileError,
  permissionsError,
  isProfileFetching,
}: UserReadTelemetryParams): void => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const accountId = account?.homeAccountId;
  const hasNoProfile = profileError?.status === 404;

  // Declared ahead of the failure logs so it runs before them in the same
  // commit: a failure is never logged without the account it belongs to.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the account ID; MSAL returns a new account object on every read
  useEffect(() => {
    if (!account) {
      return;
    }
    setLoginAccountContext(
      account,
      authStorage.auth_type.getAuthType(),
      accounts.length
    );
  }, [accountId]);

  useEffect(() => {
    if (!profileError) {
      return;
    }
    logUserReadFailure(LoginRead.PROFILE, profileError, hasNoProfile);
  }, [profileError, hasNoProfile]);

  // A user without a profile has no permissions either, so the permissions
  // failure is only logged once the profile read says whether that is why.
  useEffect(() => {
    if (!permissionsError || isProfileFetching) {
      return;
    }
    logUserReadFailure(LoginRead.PERMISSIONS, permissionsError, hasNoProfile);
  }, [permissionsError, isProfileFetching, hasNoProfile]);
};
